/* eslint-disable max-lines */
import { useId, useState } from "react"

import { useTranslation } from "react-i18next"

import type { HealthCheckResponse } from "@/api/admin-api"

import {
  Activity,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Cloud,
  Database,
  FileText,
  HardDrive,
  Key,
  RefreshCw,
  Search,
  Server,
  Settings,
  XCircle,
  Zap,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { DeploymentSwitchesCard } from "@/features/admin-dashboard/components/deployment-switches-card"
import { EgressDiagnosticsCard } from "@/features/admin-dashboard/components/egress-diagnostics-card"
import {
  useAdminSystemHealthQuery,
  useDeploymentSwitchesQuery,
  useHealthCheckQuery,
} from "@/features/admin-dashboard/hooks/admin-queries"
import { cn } from "@/lib/utils"

import { formatServiceCheckTime } from "./admin-system-health-utils"

const REFRESH_INTERVALS = [
  { value: 5000, label: "5 seconds" },
  { value: 10000, label: "10 seconds" },
  { value: 30000, label: "30 seconds" },
  { value: 60000, label: "1 minute" },
  { value: 0, label: "Manual only" },
] as const

interface ServiceInfo {
  status: string
  latency_ms?: number
  error?: string
  details?: Record<string, unknown>
}

function getExpandIcon(hasDetails: boolean, isExpanded: boolean) {
  if (!hasDetails) {
    return <div className="size-4" />
  }
  if (isExpanded) {
    return <ChevronDown className="text-muted-foreground size-4" />
  }
  return <ChevronRight className="text-muted-foreground size-4" />
}

function ServiceRow({ service, info }: { service: string; info: ServiceInfo }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const detailsId = useId()
  const hasDetails = Boolean(info.details && Object.keys(info.details).length > 0)

  const getIcon = () => {
    if (service.includes("search")) return <Search className="size-5" />
    if (service.includes("cosmos")) return <Database className="size-5" />
    if (service.includes("blob")) return <HardDrive className="size-5" />
    if (service.includes("queue")) return <Activity className="size-5" />
    if (service.includes("openai")) return <Zap className="size-5" />
    if (service.includes("key_vault")) return <Key className="size-5" />
    if (service.includes("app_config")) return <Settings className="size-5" />
    if (service.includes("function")) return <Cloud className="size-5" />
    if (service.includes("document")) return <FileText className="size-5" />
    if (service.includes("service_desk") || service.includes("servicedesk"))
      return <FileText className="size-5" />
    if (service.includes("admin")) return <Server className="size-5" />
    return <CheckCircle className="size-5" />
  }

  const getStatusColor = () => {
    if (info.status === "ok") return "text-success"
    if (info.status === "error") return "text-destructive"
    if (info.status === "degraded") return "text-warning-ink"
    return "text-muted-foreground"
  }

  const getStatusIcon = () => {
    if (info.status === "ok") return <CheckCircle className="text-success size-4" />
    if (info.status === "error") return <XCircle className="text-destructive size-4" />
    if (info.status === "degraded") return <AlertCircle className="text-warning-ink size-4" />
    return <div className="border-muted size-4 rounded-full border-2" />
  }

  const formatValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined) return "—"
    if (typeof value === "boolean") return value ? "Yes" : "No"
    if (typeof value === "number") return value.toLocaleString()
    if (Array.isArray(value)) {
      if (value.length === 0) return "—"
      // For arrays of objects (like containers or indexes)
      if (typeof value[0] === "object") return `${value.length} items`
      // For arrays of strings (like IPs or deployments)
      return value.join(", ")
    }
    return String(value)
  }

  const renderDetailValue = (key: string, value: unknown) => {
    // Special handling for containers array
    if (key === "containers" && Array.isArray(value)) {
      return (
        <div className="mt-1 space-y-1">
          {(value as Array<{ name: string; exists?: boolean; error?: string }>).map((container) => (
            <div key={container.name} className="flex items-center gap-2 text-xs">
              {container.exists ? (
                <CheckCircle className="text-success size-3" />
              ) : (
                <XCircle className="text-destructive size-3" />
              )}
              <span className={container.exists ? "" : "text-destructive"}>{container.name}</span>
              {container.error && (
                <span className="text-destructive" title={container.error}>
                  (error)
                </span>
              )}
            </div>
          ))}
        </div>
      )
    }

    // Special handling for indexes array
    if (key === "indexes" && Array.isArray(value)) {
      return (
        <div className="mt-1 space-y-1">
          {(
            value as Array<{ id: string; status: string; document_count?: number; error?: string }>
          ).map((index) => (
            <div key={index.id} className="flex items-center gap-2 text-xs">
              {index.status === "ok" ? (
                <CheckCircle className="text-success size-3" />
              ) : (
                <XCircle className="text-destructive size-3" />
              )}
              <span className={index.status === "ok" ? "" : "text-destructive"}>{index.id}</span>
              {index.document_count !== undefined && (
                <span className="text-muted-foreground">
                  ({index.document_count.toLocaleString()} docs)
                </span>
              )}
              {index.error && (
                <span className="text-destructive" title={index.error}>
                  (error)
                </span>
              )}
            </div>
          ))}
        </div>
      )
    }

    // Special handling for resolved_ips array
    if (key === "resolved_ips" && Array.isArray(value)) {
      return (
        <span className="font-mono text-xs">
          {(value as string[]).map((ip, i) => (
            <span key={ip}>
              {i > 0 && ", "}
              {ip}
            </span>
          ))}
        </span>
      )
    }

    // Special handling for deployments array
    if (key === "deployments" && Array.isArray(value)) {
      return (
        <div className="mt-1 flex flex-wrap gap-1">
          {(value as string[]).map((dep) => (
            <span key={dep} className="bg-muted rounded px-1.5 py-0.5 text-xs">
              {dep}
            </span>
          ))}
        </div>
      )
    }

    // Default: simple value
    return <span className="text-muted-foreground">{formatValue(key, value)}</span>
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <button
        aria-controls={hasDetails ? detailsId : undefined}
        aria-expanded={hasDetails ? isExpanded : undefined}
        disabled={!hasDetails}
        type="button"
        className={cn(
          "flex w-full items-center justify-between p-3 text-left transition-colors",
          hasDetails && "hover:bg-muted/50",
          isExpanded && "border-b"
        )}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        <div className="flex min-w-0 items-center gap-2">
          {getExpandIcon(hasDetails, isExpanded)}
          <div className={getStatusColor()}>{getIcon()}</div>
          <span className="min-w-0 truncate font-medium capitalize">
            {service.replace(/_/g, " ")}
          </span>
        </div>
        <div className="ml-2 flex shrink-0 items-center gap-2">
          {info.error && (
            <span
              className="text-destructive hidden max-w-40 truncate text-xs sm:inline"
              title={info.error}
            >
              {info.error}
            </span>
          )}
          {getStatusIcon()}
          {info.latency_ms !== undefined && (
            <span className="text-muted-foreground min-w-12 text-right text-xs">
              {info.latency_ms.toFixed(0)}ms
            </span>
          )}
        </div>
      </button>

      {isExpanded && info.details && (
        <div className="bg-muted/30 p-3" id={detailsId}>
          <div className="grid gap-2 text-sm">
            {Object.entries(info.details).map(([key, value]) => {
              // Skip arrays that are rendered specially
              const isComplexArray =
                Array.isArray(value) &&
                (key === "containers" || key === "indexes" || key === "deployments")

              return (
                <div key={key} className={isComplexArray ? "" : "flex items-start gap-2"}>
                  <span className="text-muted-foreground font-medium">
                    {key.replace(/_/g, " ")}:
                  </span>
                  {renderDetailValue(key, value)}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function getOverallStatusBadge(status: string) {
  if (status === "healthy") {
    return <Badge variant="success">{status}</Badge>
  }
  if (status === "degraded") {
    return <Badge variant="warning">{status}</Badge>
  }
  if (status === "unhealthy") {
    return <Badge variant="error">{status}</Badge>
  }
  return <Badge variant="disabled">{status}</Badge>
}

function renderServicesContent(isLoading: boolean, healthData: HealthCheckResponse | undefined) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg border p-3">
            <Skeleton className="size-5" />
            <Skeleton className="h-4 w-32" />
            <div className="ml-auto flex gap-2">
              <Skeleton className="h-4 w-12" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (healthData) {
    return (
      <div className="space-y-2">
        {Object.entries(healthData.services).map(([service, info]) => (
          <ServiceRow key={service} info={info as ServiceInfo} service={service} />
        ))}
      </div>
    )
  }

  return (
    <div className="text-muted-foreground flex flex-col items-center py-8 text-center">
      <p>Click "Check Services" to check service status</p>
    </div>
  )
}

export function AdminSystemHealthTab() {
  const { t } = useTranslation()
  const [systemRefreshInterval, setSystemRefreshInterval] = useState<number>(10000)
  const { data, isLoading, refetch, isRefetching } =
    useAdminSystemHealthQuery(systemRefreshInterval)

  // Services use manual refresh only (0 = disabled)
  const {
    data: healthData,
    isLoading: isLoadingHealth,
    refetch: refetchHealth,
    isRefetching: isRefetchingHealth,
  } = useHealthCheckQuery(0)
  const { data: deploymentData } = useDeploymentSwitchesQuery()
  const missingLibraryCount = deploymentData?.library_storage_mismatch.length ?? 0
  const checkedAt = formatServiceCheckTime(healthData?.timestamp)
  const degradedDescription =
    missingLibraryCount === 1
      ? t(
          "adminDashboard.workspace.operations.degradedBodyOne",
          "One library has search results whose files are missing from this stack's local storage. Some citations can return 404 and searches can time out."
        )
      : t(
          "adminDashboard.workspace.operations.degradedBody",
          "{{count}} libraries have search results whose files are missing from this stack's local storage. Some citations can return 404 and searches can time out.",
          { count: missingLibraryCount }
        )

  const getColorClass = (percent: number) => {
    if (percent < 60) return "text-success"
    if (percent < 80) return "text-warning-ink"
    return "text-destructive"
  }

  const getProgressColor = (percent: number) => {
    if (percent < 60) return "bg-success-ink"
    if (percent < 80) return "bg-warning-ink"
    return "bg-destructive-ink"
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (!data?.uptime) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="text-muted-foreground mb-4 size-12" />
          <p className="text-muted-foreground">No system health data available</p>
          {data && (
            <pre className="text-muted-foreground mt-2 text-xs">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
          <Button className="mt-4" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 size-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {missingLibraryCount > 0 ? (
        <div className="border-warning bg-warning/10 rounded-lg border p-4" role="alert">
          <div className="flex flex-wrap items-center gap-2 font-semibold">
            <AlertCircle aria-hidden="true" className="size-5" />
            {t(
              "adminDashboard.workspace.operations.degradedTitle",
              "Search and citations need attention"
            )}
          </div>
          <p className="mt-1 text-sm">{degradedDescription}</p>
          <a
            className="focus-visible:ring-ring mt-2 inline-block text-sm font-semibold underline focus-visible:ring-2"
            href="#deployment-switches"
          >
            {t(
              "adminDashboard.workspace.operations.degradedLink",
              "View affected libraries and operator guidance"
            )}
          </a>
        </div>
      ) : null}
      {/* System Health Section - with auto-refresh controls */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">System Metrics</h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <Clock className="size-4" />
              <span>
                {systemRefreshInterval === 0
                  ? "Auto-refresh disabled"
                  : `Every ${REFRESH_INTERVALS.find((i) => i.value === systemRefreshInterval)?.label}`}
              </span>
              {isRefetching && (
                <RefreshCw className="text-muted-foreground size-3.5 animate-spin" />
              )}
            </div>
            <Select
              value={systemRefreshInterval.toString()}
              onValueChange={(value) => setSystemRefreshInterval(Number(value))}
            >
              <SelectTrigger className="min-h-11 w-full sm:w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REFRESH_INTERVALS.map((interval) => (
                  <SelectItem key={interval.value} value={interval.value.toString()}>
                    {interval.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="min-h-11 w-full sm:w-auto"
              disabled={isRefetching}
              size="sm"
              variant="outline"
              onClick={() => refetch()}
            >
              <RefreshCw className={cn("mr-2 size-4", isRefetching && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Uptime Card */}
        <Card className="relative">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="size-5" />
              System Uptime
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="text-4xl font-bold">{data.uptime.uptime_formatted}</div>
                <div className="text-muted-foreground mt-2 text-sm">
                  Started: {new Date(data.uptime.start_time).toLocaleString()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resources - Full Width */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="size-5" />
              Resources
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">Memory Usage</span>
                <span
                  className={cn(
                    "text-sm font-semibold",
                    getColorClass(data.resources.memory_percent)
                  )}
                >
                  {data.resources.memory_percent.toFixed(1)}%
                </span>
              </div>
              <Progress
                className="h-2"
                indicatorClassName={getProgressColor(data.resources.memory_percent)}
                value={data.resources.memory_percent}
              />
              <div className="text-muted-foreground mt-1 text-xs">
                {data.resources.memory_used_mb.toFixed(0)} MB /{" "}
                {data.resources.memory_total_mb.toFixed(0)} MB
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">CPU Usage</span>
                <span
                  className={cn("text-sm font-semibold", getColorClass(data.resources.cpu_percent))}
                >
                  {data.resources.cpu_percent.toFixed(1)}%
                </span>
              </div>
              <Progress
                className="h-2"
                indicatorClassName={getProgressColor(data.resources.cpu_percent)}
                value={data.resources.cpu_percent}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Connected Services Section - manual refresh only */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">Connected Services</h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            {healthData?.timestamp && (
              <span className="text-muted-foreground text-sm">
                Checked {checkedAt ?? "time unavailable"}
                {healthData.check_duration_ms && ` (${healthData.check_duration_ms.toFixed(0)}ms)`}
              </span>
            )}
            <Button
              className="min-h-11 w-full sm:w-auto"
              disabled={isRefetchingHealth}
              size="sm"
              variant="outline"
              onClick={() => refetchHealth()}
            >
              <RefreshCw className={cn("mr-2 size-4", isRefetchingHealth && "animate-spin")} />
              Check Services
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="size-5" />
              Service Status
              {healthData && getOverallStatusBadge(healthData.status)}
            </CardTitle>
          </CardHeader>
          <CardContent>{renderServicesContent(isLoadingHealth, healthData)}</CardContent>
        </Card>

        <EgressDiagnosticsCard />
      </div>

      {/* Effective deployment switches (spec R6.5): a read-only sibling of the
          uptime and egress cards, not a management section — the management
          registry only carries "configuration" and "access-audit" areas. */}
      <div id="deployment-switches">
        <DeploymentSwitchesCard />
      </div>
    </div>
  )
}
